// Rituals (GDD §19.7, §22.3): the card plants a destructible ritual standard which
// channels for channelSeconds. Killing the standard cancels the ritual and refunds a
// fraction of the cost; completion fires a per-ritual resolution.

import { factionDef } from '../../content/factions';
import { cardDef, defs, ritualDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import type { PlayerId, RitualDef } from '../../content/types';
import { dist, type Vec2 } from '../../shared/vec';
import type { Sim } from '../sim';

const dt = TUNING.dt;

/** Is pos a legal channel location for this ritual? */
export function validRitualPos(sim: Sim, player: PlayerId, rd: RitualDef, pos: Vec2): boolean {
  const inCore = (() => {
    const core = sim.coreAlive(player);
    return !!core && dist(pos, core.pos) <= sim.map.coreTerritoryRadius;
  })();
  const atSite = sim.state.sites.some((s) => s.owner === player && dist(pos, s.pos) <= TUNING.buildRadius);
  switch (rd.channelAt) {
    case 'core-territory':
      return inCore;
    case 'controlled-site':
      return atSite;
    case 'both':
      return inCore || atSite;
  }
}

/** Any legal channel location at all (for canPlayBase's location check)? */
export function anyValidRitualLocation(sim: Sim, player: PlayerId, rd: RitualDef): boolean {
  if (rd.channelAt !== 'controlled-site' && sim.coreAlive(player)) return true;
  if (rd.channelAt !== 'core-territory') {
    for (const s of sim.state.sites) if (s.owner === player) return true;
  }
  return false;
}

/** Plant the standard and start the channel. Caller has already validated and committed cost. */
export function startRitual(sim: Sim, player: PlayerId, cardId: string, pos: Vec2): void {
  const def = cardDef(cardId);
  const rd = ritualDef(def.subjectId);
  const standardDefId = factionDef(sim.state.players[player].faction).ritualStandardDefId;
  const standard = sim.spawnBuilding(standardDefId, player, pos, { complete: true, cardId });
  standard.maxHealth = rd.standardHealth;
  standard.health = rd.standardHealth;
  sim.state.rituals.push({
    standardId: standard.id,
    ritualId: rd.id,
    cardId,
    owner: player,
    remaining: rd.channelSeconds,
  });
  sim.pushEvent({ type: 'RitualStarted', player, ritualId: rd.id, pos: { ...pos } });
}

export function stepRituals(sim: Sim): void {
  const st = sim.state;
  if (st.rituals.length === 0) return;
  for (let i = st.rituals.length - 1; i >= 0; i--) {
    const r = st.rituals[i];
    const standard = st.buildings.get(r.standardId);
    if (!standard || standard.state === 'destroyed') {
      // standard died: cancel + partial refund (uses stay spent)
      const def = cardDef(r.cardId);
      const rd = ritualDef(def.subjectId);
      const p = st.players[r.owner];
      p.power += Math.round(def.cost.power * rd.refundFraction);
      p.essence += Math.round(def.cost.essence * rd.refundFraction);
      sim.pushEvent({
        type: 'RitualCancelled',
        player: r.owner,
        ritualId: r.ritualId,
        pos: standard ? { ...standard.pos } : { x: 0, y: 0 },
      });
      st.rituals.splice(i, 1);
      continue;
    }
    r.remaining -= dt;
    if (r.remaining <= 0) {
      const pos = { ...standard.pos };
      resolveRitual(sim, r.ritualId, r.owner, pos);
      sim.pushEvent({ type: 'RitualComplete', player: r.owner, ritualId: r.ritualId, pos });
      st.rituals.splice(i, 1);
      // the standard burns out once the ritual resolves
      sim.destroyBuildingSilently(standard);
    }
  }
}

/** Per-ritual resolution — the unique payloads live here, keyed by ritual id. */
function resolveRitual(sim: Sim, ritualId: string, owner: PlayerId, pos: Vec2): void {
  switch (ritualId) {
    case 'rit-ic-march-of-the-last-wall': {
      // 35 s of spectral shield plates on every existing non-boss squad, plus a speed
      // sync so the army marches as one. (Bible IC-22; sub-20% shield proc simplified
      // into the up-front plates — documented deviation.)
      for (const s of sim.state.squads.values()) {
        if (s.owner !== owner || s.isBoss) continue;
        s.tempHealth = Math.max(s.tempHealth, 250);
        s.tempHealthRemaining = 35;
        s.buffs.push({ stat: 'moveSpeed', mult: 1.1, remaining: 35 });
      }
      break;
    }
    case 'rit-vw-great-awakening': {
      // 32 s: living structures regenerate through light damage; squads holding
      // sites fight harder. (Worker speed/radius bonuses simplified out — deviation.)
      const p = sim.state.players[owner];
      p.awakeningRemaining = 32;
      for (const s of sim.state.squads.values()) {
        if (s.owner !== owner) continue;
        if (s.command.kind === 'defend' || s.command.kind === 'capture') {
          s.buffs.push({ stat: 'attackSpeed', mult: 1.15, remaining: 32 });
        }
      }
      break;
    }
    case 'rit-ah-night-of-the-unburied': {
      // Up to the 4 most recent non-boss Ashen squad deaths return as spectral
      // versions (55% health, 40 s). Support/economy specialists come back as shades.
      const deaths = sim.state.recentDeaths
        .filter((d) => d.owner === owner && !d.wasBoss)
        .slice(-4);
      let i = 0;
      for (const d of deaths) {
        const ud = defs.units.get(d.defId);
        const isSpecialist = !ud || ud.roleTags.includes('support') || ud.roleTags.includes('worker');
        const spawnId = isSpecialist ? 'u-ah-shade' : d.defId;
        const angle = (i++ / Math.max(1, deaths.length)) * Math.PI * 2;
        sim.spawnSquad(
          spawnId,
          owner,
          { x: pos.x + Math.cos(angle) * 4, y: pos.y + Math.sin(angle) * 4 },
          { temporarySeconds: 40, maxHealthMult: 0.55 },
        );
      }
      break;
    }
    case 'rit-ad-convergence-protocol': {
      // 30 s perfect network: faster production, Asc I–II summons at linked conduits,
      // and marching speed for the army (instant-delivery bonus simplified — deviation)
      const p = sim.state.players[owner];
      p.convergenceRemaining = 30;
      for (const s of sim.state.squads.values()) {
        if (s.owner !== owner) continue;
        if (s.command.kind === 'escort' || s.command.kind === 'defend' || s.command.kind === 'move') {
          s.buffs.push({ stat: 'moveSpeed', mult: 1.15, remaining: 30 });
        }
      }
      break;
    }
    default:
      void pos;
      break;
  }
}
